import React from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  List,
  Tag,
  Button,
  Space,
  Typography,
  Spin,
  Avatar,
  Table,
  Progress,
  Badge,
  Tooltip,
  Alert,
} from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  WarningOutlined,
  TrophyOutlined,
  FireOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';
  const isCompanyScopedPartner =
    user?.role === 'agent_partner' || user?.role === 'sales_rep_associated';
  const [selectedDay, setSelectedDay] = React.useState<string | null>(
    isAdmin ? dayjs().format('YYYY-MM-DD') : null
  );
  const [ticketsPage, setTicketsPage] = React.useState(1);
  const [ticketsPageSize, setTicketsPageSize] = React.useState(10);

  // Fetch dashboard data (analytics summary + by day)
  const { data: dashboardData, isLoading, refetch } = useQuery('dashboard', async () => {
    const [summaryRes, byDayRes] = await Promise.all([
      api.get('/api/analytics/summary'),
      api.get('/api/analytics/tickets-by-day', { params: { days: 7 } }),
    ]);
    return {
      statusCounts: summaryRes.data.statusCounts,
      totals: summaryRes.data.totals,
      byDay: byDayRes.data.days,
    };
  }, { refetchInterval: 15000 });

  // Fetch agent performance metrics (disabled for admins)
  const { data: myPerformance, isLoading: performanceLoading } = useQuery(
    'my-performance',
    () => api.get('/api/dashboard/my-performance').then(res => res.data),
    { enabled: !!user && !isAdmin }
  );


  // Fetch recent tickets
  const { data: recentTickets = [] } = useQuery('recentTickets', async () => {
    const response = await api.get('/api/tickets', {
      params: { limit: 10, sort: 'created_at', order: 'desc' }
    });
    return response.data.tickets;
  }, { refetchInterval: 15000 });

  // Fetch my tickets
  const { data: myTickets = [] } = useQuery('myTickets', async () => {
    const response = await api.get('/api/tickets', {
      params: { assigned_to: user?.id, limit: 10 }
    });
    return response.data.tickets;
  }, { refetchInterval: 15000 });

  /** Partner roles: count active (non-solved) tickets visible to the company — assignee is usually internal. */
  const { data: companyActiveTicketsTotal = 0 } = useQuery(
    ['company-active-tickets-total', user?.company_id],
    async () => {
      const [openR, pendingR, newR] = await Promise.all([
        api.get('/api/tickets', { params: { page: 1, limit: 1, status: 'open' } }),
        api.get('/api/tickets', { params: { page: 1, limit: 1, status: 'pending' } }),
        api.get('/api/tickets', { params: { page: 1, limit: 1, status: 'new' } }),
      ]);
      return (
        (openR.data?.total || 0) +
        (pendingR.data?.total || 0) +
        (newR.data?.total || 0)
      );
    },
    { enabled: !!user && isCompanyScopedPartner, refetchInterval: 15000 }
  );

  // Weekly activity from analytics endpoint
  const weeklyActivityData = (dashboardData?.byDay || []).map((d: any) => ({
    name: dayjs(d.day).format('ddd'),
    date: dayjs(d.day).format('MMM D'),
    fullDate: d.day,
    tickets: d.created,
    solved: d.solved,
  }));

  // Fetch tickets for selected day (admin only)
  const { data: dayTickets, isLoading: dayTicketsLoading } = useQuery(
    ['day-tickets', selectedDay, ticketsPage, ticketsPageSize],
    () => {
      if (!selectedDay) {
        return { tickets: [], total: 0 };
      }
      // Use just the date string (YYYY-MM-DD) for filtering, SQL DATE() function will handle the comparison
      const dateStr = selectedDay; // Already in YYYY-MM-DD format
      return api.get('/api/tickets', {
        params: { 
          from: dateStr,
          to: dateStr,
          page: ticketsPage,
          limit: ticketsPageSize,
          sort: 'created_at',
          order: 'desc'
        }
      }).then(res => res.data);
    },
    { 
      enabled: isAdmin && !!selectedDay,
      retry: false,
      refetchOnWindowFocus: false,
      refetchInterval: 15000
    }
  );

  // Set default selected day to today for admins
  React.useEffect(() => {
    if (isAdmin && !selectedDay) {
      const today = dayjs().format('YYYY-MM-DD');
      setSelectedDay(today);
    }
  }, [isAdmin, selectedDay]);

  // My performance trends (last 7 days)
  const myTrendsData = React.useMemo(() => {
    if (!myPerformance?.trends) return [];
    const trendsMap = new Map();
    myPerformance.trends.forEach((t: any) => {
      trendsMap.set(t.day, t.solved_count);
    });
    
    // Fill in missing days with 0
    const result = [];
    for (let i = 6; i >= 0; i--) {
      const date = dayjs().subtract(i, 'day').format('YYYY-MM-DD');
      result.push({
        name: dayjs(date).format('ddd'),
        solved: trendsMap.get(date) || 0,
      });
    }
    return result;
  }, [myPerformance]);

  // Priority breakdown data
  const priorityData = React.useMemo(() => {
    if (!myPerformance?.priorityBreakdown) return [];
    return [
      { name: 'High', value: myPerformance.priorityBreakdown.high || 0, color: '#ff4d4f' },
      { name: 'Medium', value: myPerformance.priorityBreakdown.medium || 0, color: '#faad14' },
      { name: 'Low', value: myPerformance.priorityBreakdown.low || 0, color: '#52c41a' },
    ].filter(item => item.value > 0);
  }, [myPerformance]);

  const getStatusColor = (status: string) => {
    const colors: any = {
      new: 'default',
      open: 'blue',
      pending: 'orange',
      solved: 'green',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: any = {
      low: 'green',
      medium: 'orange',
      high: 'red',
    };
    return colors[priority] || 'default';
  };

  // Chart data
  const ticketStatusData = [
    { name: 'New', value: dashboardData?.statusCounts?.new || 0, color: '#9E9E9E' },
    { name: 'Open', value: dashboardData?.statusCounts?.open || 0, color: '#2196F3' },
    { name: 'Pending', value: dashboardData?.statusCounts?.pending || 0, color: '#FF9800' },
    { name: 'Solved', value: dashboardData?.statusCounts?.solved || 0, color: '#4CAF50' },
  ];

  // Format time helper
  const formatTime = (minutes: number) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };


  if (isLoading || (!isAdmin && performanceLoading)) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Loading dashboard...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: '#f0f2f5' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              Welcome back, {user?.first_name}!
            </Title>
            <Text type="secondary">
              {isAdmin
                ? "Here's your support overview for today"
                : 'Your workload, queues, and personal metrics'}
            </Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => {
              refetch();
              window.location.reload();
            }}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/workspace/new')}>
              New Ticket
            </Button>
          </Space>
        </div>
      </div>

      {isCompanyScopedPartner && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          message="Visibility vs. assignment"
          description={
            <>
              You can see every ticket for businesses linked to your company. Internal agents are usually the assignee
              while they work the issue — use <strong>Tickets → Company tickets</strong> for the full list.{' '}
              <strong>Assigned to me</strong> and <strong>Unassigned</strong> above only reflect your personal queues.
            </>
          }
        />
      )}

      {/* Key Metrics - Enhanced - Hidden for admins */}
      {!isAdmin && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ height: '100%', minHeight: 140 }}>
              <Statistic
                title={isCompanyScopedPartner ? 'Active company tickets' : 'My Open Tickets'}
                value={
                  isCompanyScopedPartner
                    ? companyActiveTicketsTotal
                    : myPerformance?.today?.open || myTickets.filter((t: any) => t.status !== 'solved').length
                }
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
              <div style={{ marginTop: 8, minHeight: 24 }}>
                {isCompanyScopedPartner ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    New, open, or pending for your company’s businesses
                  </Text>
                ) : myPerformance?.today?.highPriorityOpen > 0 ? (
                  <Tag color="red">
                    <FireOutlined /> {myPerformance.today.highPriorityOpen} High Priority
                  </Tag>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>All priorities normal</Text>
                )}
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ height: '100%', minHeight: 140 }}>
              <Statistic
                title="Solved Today"
                value={myPerformance?.today?.solved || 0}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
              <div style={{ marginTop: 8, minHeight: 24 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {myPerformance?.thisWeek?.solved || 0} this week
                </Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ height: '100%', minHeight: 140 }}>
              <Statistic
                title="Avg Resolution Time"
                value={myPerformance?.thisWeek?.avgResolutionHours || 0}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#faad14' }}
                suffix="hours"
              />
              <div style={{ marginTop: 8, minHeight: 24 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  This week
                </Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card style={{ height: '100%', minHeight: 140 }}>
              <Statistic
                title="First Response Time"
                value={formatTime(myPerformance?.thisWeek?.avgFirstResponseMinutes || 0)}
                prefix={<ThunderboltOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
              <div style={{ marginTop: 8, minHeight: 24 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Average this week
                </Text>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {!isAdmin && (
        <Card size="small" style={{ marginBottom: 24 }} title="At a glance — open by priority">
          <Space wrap size="large">
            <Statistic
              title="High"
              value={myPerformance?.priorityBreakdown?.high ?? 0}
              valueStyle={{ color: '#ff4d4f' }}
            />
            <Statistic
              title="Medium"
              value={myPerformance?.priorityBreakdown?.medium ?? 0}
              valueStyle={{ color: '#faad14' }}
            />
            <Statistic
              title="Low"
              value={myPerformance?.priorityBreakdown?.low ?? 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Space>
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            Open tickets assigned to you (new, open, or pending). Charts below show trends and the same split visually.
          </Text>
        </Card>
      )}

      {/* Priority Widgets — queues (non-admin) */}
      {!isAdmin && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={8}>
            <Card
              title={isCompanyScopedPartner ? 'Assigned to me' : 'My Tickets'}
              extra={<Button type="link" onClick={() => navigate('/tickets?tab=my')}>View All</Button>}
              style={{ height: '100%', minHeight: 400 }}
              bodyStyle={{ height: 'calc(100% - 57px)', overflow: 'auto' }}
            >
              <List
                dataSource={myTickets.filter((t: any) => t.status !== 'solved').slice(0, 4)}
                renderItem={(ticket: any) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/workspace/number/${ticket.ticket_number}`)}
                  >
                    <List.Item.Meta
                      avatar={<Avatar style={{ backgroundColor: '#1890ff' }}><UserOutlined /></Avatar>}
                      title={
                        <Space>
                          <Text strong>#{ticket.ticket_number}</Text>
                          <Text ellipsis style={{ maxWidth: 120 }}>{ticket.subject}</Text>
                        </Space>
                      }
                      description={
                        <Space>
                          <Tag color={getStatusColor(ticket.status)}>{ticket.status}</Tag>
                          <Tag color={getPriorityColor(ticket.priority)}>{ticket.priority}</Tag>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
              {myTickets.filter((t: any) => t.status !== 'solved').length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#999', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text type="secondary">No assigned tickets</Text>
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card
              title="Unassigned Tickets"
              extra={<Button type="link" onClick={() => navigate('/tickets?tab=unassigned')}>View All</Button>}
              style={{ height: '100%', minHeight: 400 }}
              bodyStyle={{ height: 'calc(100% - 57px)', overflow: 'auto' }}
            >
              <List
                dataSource={recentTickets.filter((t: any) => !t.assigned_to).slice(0, 4)}
                renderItem={(ticket: any) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/workspace/number/${ticket.ticket_number}`)}
                  >
                    <List.Item.Meta
                      avatar={<Avatar style={{ backgroundColor: '#ff4d4f' }}>!</Avatar>}
                      title={
                        <Space>
                          <Text strong>#{ticket.ticket_number}</Text>
                          <Text ellipsis style={{ maxWidth: 120 }}>{ticket.subject}</Text>
                        </Space>
                      }
                      description={
                        <Space>
                          <Tag color={getPriorityColor(ticket.priority)}>{ticket.priority}</Tag>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {dayjs(ticket.created_at).fromNow()}
                          </Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
              {recentTickets.filter((t: any) => !t.assigned_to).length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#999', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text type="secondary">No unassigned tickets</Text>
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card
              title="SLA Alerts"
              extra={<WarningOutlined style={{ color: '#faad14' }} />}
              style={{ height: '100%', minHeight: 400 }}
              bodyStyle={{ height: 'calc(100% - 57px)', overflow: 'auto' }}
            >
              <List
                dataSource={recentTickets.filter((t: any) => {
                  const hoursSinceCreated = dayjs().diff(dayjs(t.created_at), 'hours');
                  return hoursSinceCreated > 24 && t.status !== 'solved';
                }).slice(0, 4)}
                renderItem={(ticket: any) => {
                  const hoursSinceCreated = dayjs().diff(dayjs(ticket.created_at), 'hours');
                  return (
                    <List.Item
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/workspace/number/${ticket.ticket_number}`)}
                    >
                      <List.Item.Meta
                        avatar={<Avatar style={{ backgroundColor: '#faad14' }}><ExclamationCircleOutlined /></Avatar>}
                        title={
                          <Space>
                            <Text strong>#{ticket.ticket_number}</Text>
                            <Text ellipsis style={{ maxWidth: 120 }}>{ticket.subject}</Text>
                          </Space>
                        }
                        description={
                          <Space>
                            <Tag color={getPriorityColor(ticket.priority)}>{ticket.priority}</Tag>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {hoursSinceCreated}h old
                            </Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  );
                }}
              />
              {recentTickets.filter((t: any) => {
                const hoursSinceCreated = dayjs().diff(dayjs(t.created_at), 'hours');
                return hoursSinceCreated > 24 && t.status !== 'solved';
              }).length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#999', minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text type="secondary">No SLA alerts</Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* Daily Ticket Stats - Admin Only */}
      {isAdmin && (
        <>
          {/* Selected Day Tickets - Always show for admins */}
          {selectedDay && (() => {
            const dayData = weeklyActivityData.find((d: any) => d.fullDate === selectedDay);
            const createdCount = dayData?.tickets || 0;
            const solvedCount = dayData?.solved || 0;
            return (
              <Card 
                title={
                  <Space>
                    <FileTextOutlined />
                    <Text strong>
                      Tickets for {dayjs(selectedDay).format('MMMM D, YYYY')} ({dayjs(selectedDay).format('dddd')})
                    </Text>
                    <Text type="secondary" style={{ fontSize: 14, fontWeight: 'normal' }}>
                      • Created: <Text strong style={{ color: '#1890ff' }}>{createdCount}</Text>
                      {' '}• Solved: <Text strong style={{ color: '#52c41a' }}>{solvedCount}</Text>
                    </Text>
                  </Space>
                }
              style={{ marginBottom: 24 }}
              extra={
                <Button type="link" onClick={() => navigate(`/tickets?from=${selectedDay}&to=${selectedDay}`)}>
                  View All
                </Button>
              }
            >
              {dayTicketsLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Spin />
                </div>
              ) : dayTickets?.tickets && dayTickets.tickets.length > 0 ? (
                <Table
                  dataSource={dayTickets.tickets}
                  rowKey="id"
                  pagination={{
                    current: ticketsPage,
                    pageSize: ticketsPageSize,
                    total: dayTickets?.total || 0,
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tickets`,
                    onChange: (page, pageSize) => {
                      setTicketsPage(page);
                      setTicketsPageSize(pageSize);
                    },
                    onShowSizeChange: (current, size) => {
                      setTicketsPage(1);
                      setTicketsPageSize(size);
                    }
                  }}
                  size="small"
                  onRow={(record) => ({
                    onClick: () => navigate(`/workspace/number/${record.ticket_number}`),
                    style: { cursor: 'pointer' }
                  })}
                  columns={[
                    {
                      title: 'Ticket #',
                      dataIndex: 'ticket_number',
                      key: 'ticket_number',
                      width: 120,
                      render: (text: string) => (
                        <Text strong style={{ color: '#1890ff' }}>#{text}</Text>
                      ),
                    },
                    {
                      title: 'Subject',
                      dataIndex: 'subject',
                      key: 'subject',
                      ellipsis: true,
                      render: (text: string) => (
                        <Text ellipsis style={{ maxWidth: 300 }}>{text}</Text>
                      ),
                    },
                    {
                      title: 'Business',
                      dataIndex: 'business_name',
                      key: 'business_name',
                      width: 180,
                      ellipsis: true,
                      render: (text: string) => text || 'N/A',
                    },
                    {
                      title: 'Status',
                      dataIndex: 'status',
                      key: 'status',
                      width: 100,
                      render: (status: string) => (
                        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
                      ),
                    },
                    {
                      title: 'Priority',
                      dataIndex: 'priority',
                      key: 'priority',
                      width: 100,
                      render: (priority: string) => (
                        <Tag color={getPriorityColor(priority)}>{priority.toUpperCase()}</Tag>
                      ),
                    },
                    {
                      title: 'Assigned To',
                      key: 'assigned_to',
                      width: 150,
                      render: (_: any, record: any) => 
                        record.assigned_to_name 
                          ? `${record.assigned_to_name} ${record.assigned_to_last_name || ''}`.trim()
                          : <Text type="secondary">Unassigned</Text>,
                    },
                    {
                      title: 'Time',
                      dataIndex: 'created_at',
                      key: 'created_at',
                      width: 100,
                      render: (date: string) => dayjs(date).format('h:mm A'),
                    },
                  ]}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                  <Text type="secondary">No tickets for this day</Text>
                </div>
              )}
            </Card>
            );
          })()}

        </>
      )}

      {/* Recent tickets + personal performance (non-admin) — before org-wide charts */}
      {!isAdmin && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card title="Recent Tickets" extra={<Button type="link" onClick={() => navigate('/tickets')}>View All</Button>}>
              <List
                dataSource={recentTickets.slice(0, 6)}
                renderItem={(ticket: any) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/workspace/number/${ticket.ticket_number}`)}
                  >
                    <List.Item.Meta
                      avatar={<Avatar>{ticket.business_name?.[0] || 'T'}</Avatar>}
                      title={
                        <Space>
                          <Text strong>#{ticket.ticket_number}</Text>
                          <Text ellipsis style={{ maxWidth: 200 }}>{ticket.subject}</Text>
                        </Space>
                      }
                      description={
                        <Space>
                          <Tag color={getStatusColor(ticket.status)}>{ticket.status}</Tag>
                          <Tag color={getPriorityColor(ticket.priority)}>{ticket.priority}</Tag>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs(ticket.created_at).fromNow()}
                          </Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      )}

      {!isAdmin && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card title="My Performance Trend (Last 7 Days)" style={{ height: '100%', minHeight: 350 }}>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={myTrendsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" style={{ fontSize: 12 }} />
                  <YAxis style={{ fontSize: 12 }} />
                  <RechartsTooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="solved" fill="#52c41a" name="Tickets Solved" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="My Open Tickets by Priority" style={{ height: '100%', minHeight: 350 }}>
              {priorityData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={priorityData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                      label={(entry) => `${entry.name}: ${entry.value}`}
                    >
                      {priorityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip contentStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#999', minHeight: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Text type="secondary">No open tickets</Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      )}

      {/* Org-wide charts */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title="Weekly Activity (All Tickets)"
            style={{ height: '100%', minHeight: 350 }}
            extra={
              !isAdmin ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Organization-wide
                </Text>
              ) : undefined
            }
          >
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={weeklyActivityData}>
                <defs>
                  <linearGradient id="colorTickets" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#1890ff" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorSolved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#52c41a" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#52c41a" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  style={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  tickFormatter={(value, index) => {
                    const dataPoint = weeklyActivityData[index];
                    if (dataPoint) {
                      return `${dataPoint.name} ${value}`;
                    }
                    return value;
                  }}
                />
                <YAxis style={{ fontSize: 12 }} />
                <RechartsTooltip 
                  contentStyle={{ fontSize: 12 }}
                  labelFormatter={(value, payload) => {
                    if (payload && payload[0] && payload[0].payload) {
                      const date = dayjs(payload[0].payload.fullDate);
                      return `${date.format('dddd, MMMM D, YYYY')}`;
                    }
                    return value;
                  }}
                />
                <Area type="monotone" dataKey="tickets" stroke="#1890ff" fillOpacity={1} fill="url(#colorTickets)" name="Created" />
                <Area type="monotone" dataKey="solved" stroke="#52c41a" fillOpacity={1} fill="url(#colorSolved)" name="Solved" />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title="Overall Ticket Status"
            style={{ height: '100%', minHeight: 350 }}
            extra={
              !isAdmin ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Organization-wide
                </Text>
              ) : undefined
            }
          >
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={ticketStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={70}
                  fill="#8884d8"
                  dataKey="value"
                  label={(entry) => `${entry.name}: ${entry.value}`}
                >
                  {ticketStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip contentStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {isAdmin && (
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card title="Recent Tickets" extra={<Button type="link" onClick={() => navigate('/tickets')}>View All</Button>}>
              <List
                dataSource={recentTickets.slice(0, 6)}
                renderItem={(ticket: any) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/workspace/number/${ticket.ticket_number}`)}
                  >
                    <List.Item.Meta
                      avatar={<Avatar>{ticket.business_name?.[0] || 'T'}</Avatar>}
                      title={
                        <Space>
                          <Text strong>#{ticket.ticket_number}</Text>
                          <Text ellipsis style={{ maxWidth: 200 }}>{ticket.subject}</Text>
                        </Space>
                      }
                      description={
                        <Space>
                          <Tag color={getStatusColor(ticket.status)}>{ticket.status}</Tag>
                          <Tag color={getPriorityColor(ticket.priority)}>{ticket.priority}</Tag>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs(ticket.created_at).fromNow()}
                          </Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Dashboard;
